import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Tv, LayoutDashboard, Radio, Video, List, Cast,
  BarChart3, Settings, LogOut, Menu, X, User, PlayCircle, Megaphone, ChevronRight, Tv2,
  Bell, Radio as RadioIcon, WifiOff, CheckCheck, Trash2, Wifi, Search, Film, ListVideo, Info, Activity
} from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import ThemeToggle from './ThemeToggle';
import pkg from '../../package.json';
const { version } = pkg;

// ── Notification helpers ───────────────────────────────────────────────────

const NOTIF_ICONS = {
  stream_live:       { icon: RadioIcon,  bg: 'bg-green-500/15',  iconColor: 'text-green-400' },
  stream_offline:    { icon: WifiOff,    bg: 'bg-gray-500/15',   iconColor: 'text-gray-400' },
  vod_ready:         { icon: Film,       bg: 'bg-blue-500/15',   iconColor: 'text-blue-400' },
  playlist_started:  { icon: ListVideo,  bg: 'bg-purple-500/15', iconColor: 'text-purple-400' },
  system:            { icon: Info,       bg: 'bg-indigo-500/15', iconColor: 'text-indigo-400' },
};

// Strip leading emoji characters from notification titles (stored with emoji prefix in DB)
const stripLeadingEmoji = (str) => {
  if (!str) return str;
  return str.replace(/^(\p{Emoji_Presentation}|\p{Extended_Pictographic})\s*/u, '').trim();
};

function timeAgo(dateStr, tz = 'UTC') {
  // SQLite CURRENT_TIMESTAMP stores UTC without timezone suffix.
  // new Date("2026-03-31 00:12:29") is parsed as LOCAL time by browsers,
  // so we normalise to ISO-8601 UTC ("...T...Z") before constructing the Date.
  const utcStr = dateStr ? dateStr.replace(' ', 'T').replace(/Z?$/, 'Z') : dateStr;
  const date = new Date(utcStr);
  const diff = (Date.now() - date.getTime()) / 1000;
  const hhmm = date.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz });
  if (diff < 60)    return hhmm;
  if (diff < 3600)  return `hace ${Math.floor(diff / 60)}m · ${hhmm}`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h · ${hhmm}`;
  const mmdd = date.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', timeZone: tz });
  return `${mmdd} · ${hhmm}`;
}

// ── NotificationPanel ──────────────────────────────────────────────────────

function NotificationPanel({ onClose, anchorRect, buttonRef }) {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  // Seed from localStorage so first render already uses correct timezone (no flash to UTC)
  const [serverTz, setServerTz] = useState(() => localStorage.getItem('serverTz') || 'UTC');
  const panelRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const [notifRes, settingsRes] = await Promise.all([
        axios.get('/api/notifications', { headers: { Authorization: `Bearer ${token}` } }),
        axios.get('/api/settings', { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
      ]);
      setNotifications(notifRes.data.notifications || []);
      setUnread(notifRes.data.unread_count || 0);
      if (settingsRes?.data?.timezone) {
        setServerTz(settingsRes.data.timezone);
        localStorage.setItem('serverTz', settingsRes.data.timezone);
      }
    } catch (_) {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Close on outside click — but NOT when clicking the bell button itself
  // (the button's own onClick handles the toggle)
  useEffect(() => {
    const handler = (e) => {
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      const inButton = buttonRef?.current && buttonRef.current.contains(e.target);
      if (!inPanel && !inButton) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose, buttonRef]);

  const markAll = async () => {
    const token = localStorage.getItem('token');
    await axios.put('/api/notifications/read-all', {}, {
      headers: { Authorization: `Bearer ${token}` }
    }).catch(() => {});
    setNotifications(n => n.map(x => ({ ...x, is_read: 1 })));
    setUnread(0);
  };

  const markOne = async (id) => {
    const token = localStorage.getItem('token');
    await axios.put(`/api/notifications/${id}/read`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    }).catch(() => {});
    setNotifications(n => n.map(x => x.id === id ? { ...x, is_read: 1 } : x));
    setUnread(u => Math.max(0, u - 1));
  };

  const clearAll = async () => {
    const token = localStorage.getItem('token');
    await axios.delete('/api/notifications', {
      headers: { Authorization: `Bearer ${token}` }
    }).catch(() => {});
    setNotifications([]);
    setUnread(0);
  };

  return (
    <div
      ref={panelRef}
      className="glass-card border border-black/10 dark:border-white/10
                 rounded-2xl shadow-2xl overflow-hidden"
      style={{
        position: 'fixed',
        width: Math.min(320, window.innerWidth - 16),
        zIndex: 99999,
        bottom: anchorRect ? window.innerHeight - anchorRect.top + 8 : 'auto',
        top: anchorRect ? 'auto' : 60,
        left: anchorRect
          ? Math.max(8, Math.min(anchorRect.left, window.innerWidth - Math.min(320, window.innerWidth - 16) - 8))
          : 8,
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-black/5 dark:border-white/5">
        <span className="font-semibold text-sm text-gray-900 dark:text-white flex items-center gap-2">
          <Bell size={15} className="text-indigo-400" />
          Notificaciones
          {unread > 0 && (
            <span className="bg-indigo-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full leading-none">
              {unread}
            </span>
          )}
        </span>
        <div className="flex items-center gap-1">
          {unread > 0 && (
            <button
              onClick={markAll}
              className="p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 text-gray-500 hover:text-indigo-400 transition-colors"
              title="Marcar todo como leído"
            >
              <CheckCheck size={15} />
            </button>
          )}
          {notifications.length > 0 && (
            <button
              onClick={clearAll}
              className="p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 text-gray-500 hover:text-red-400 transition-colors"
              title="Eliminar todas"
            >
              <Trash2 size={15} />
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="overflow-y-auto" style={{ maxHeight: 380 }}>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="loading w-6 h-6" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10 text-center px-4">
            <Bell size={28} className="text-gray-300 dark:text-gray-600" />
            <p className="text-sm text-gray-400">Sin notificaciones</p>
          </div>
        ) : (
          notifications.map(n => {
            const notifMeta = NOTIF_ICONS[n.type] || NOTIF_ICONS.system;
            const NotifIcon = notifMeta.icon;
            return (
              <div
                key={n.id}
                onClick={() => !n.is_read && markOne(n.id)}
                className={`flex items-start gap-3 px-4 py-3 border-b border-black/5 dark:border-white/5
                            last:border-0 cursor-default transition-colors
                            ${!n.is_read ? 'bg-indigo-500/5 hover:bg-indigo-500/10' : 'hover:bg-black/3 dark:hover:bg-white/3'}`}
              >
                <div className={`w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center mt-0.5 ${notifMeta.bg}`}>
                  <NotifIcon size={15} className={notifMeta.iconColor} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className={`text-xs font-semibold leading-tight ${!n.is_read ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                      {stripLeadingEmoji(n.title)}
                    </p>
                    {!n.is_read && (
                      <span className="w-2 h-2 bg-indigo-500 rounded-full flex-shrink-0 mt-0.5" />
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-snug">{n.message}</p>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">{timeAgo(n.created_at, serverTz)}</p>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ── BellButton ─────────────────────────────────────────────────────────────

function BellButton({ compact = false }) {
  const [open, setOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const [unread, setUnread] = useState(0);
  const containerRef = useRef(null);
  const eventSourceRef = useRef(null);

  const fetchUnread = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get('/api/notifications', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnread(res.data.unread_count || 0);
    } catch (_) {}
  }, []);

  // Initial load + SSE for real-time push
  useEffect(() => {
    fetchUnread();

    const token = localStorage.getItem('token');
    if (!token) return;

    // SSE connection — pass token via query param (EventSource can't set headers)
    const es = new EventSource(`/api/notifications/stream?_t=${encodeURIComponent(token)}`);
    eventSourceRef.current = es;

    es.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === 'notification') {
          setUnread(u => u + 1);
          // Play a subtle browser notification sound if supported
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification(msg.payload.title, { body: msg.payload.message, silent: true });
          }
        }
      } catch (_) {}
    };

    es.onerror = () => {
      // SSE connection lost — fall back to polling
      es.close();
    };

    // Polling fallback every 30s
    const poll = setInterval(fetchUnread, 30000);

    return () => {
      es.close();
      clearInterval(poll);
    };
  }, [fetchUnread]);

  // SSE needs auth — EventSource doesn't support headers, so we re-attach the
  // token via cookie or query param. Since the backend uses Bearer tokens we
  // use a tiny workaround: intercept in the middleware via query param.
  // (The notifications/stream route already uses authenticateToken which reads
  //  req.headers.authorization. We need to patch it to also read ?_t= param.)

  const handleClose = useCallback(() => {
    setOpen(false);
    fetchUnread(); // Refresh badge after panel closed
  }, [fetchUnread]);

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={(e) => {
          if (!open) setAnchorRect(e.currentTarget.getBoundingClientRect());
          setOpen(o => !o);
        }}
        className={`relative rounded-lg transition-all duration-200
                    bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10
                    text-gray-500 dark:text-gray-400
                    hover:bg-black/10 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white
                    ${compact ? 'p-1.5' : 'p-1.5'}`}
        title="Notificaciones"
      >
        <Bell size={compact ? 14 : 15} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-indigo-500 text-white
                           text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none
                           shadow-lg shadow-indigo-500/40">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && ReactDOM.createPortal(
        <NotificationPanel onClose={handleClose} anchorRect={anchorRect} buttonRef={containerRef} />,
        document.body
      )}
    </div>
  );
}

// ── GlobalSearch ────────────────────────────────────────────────────────────

function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  // Ctrl+K / Cmd+K global shortcut + custom event from sidebar button
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      }
    };
    const eventHandler = () => setOpen(true);
    document.addEventListener('keydown', handler);
    document.addEventListener('streamcp:open-search', eventHandler);
    return () => {
      document.removeEventListener('keydown', handler);
      document.removeEventListener('streamcp:open-search', eventHandler);
    };
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQuery('');
      setResults([]);
      setActiveIdx(0);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`/api/search?q=${encodeURIComponent(query)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        setResults(data.results || []);
        setActiveIdx(0);
      } catch (_) { setResults([]); }
      setLoading(false);
    }, 300);
  }, [query]);

  const handleSelect = useCallback((result) => {
    navigate(result.url);
    setOpen(false);
  }, [navigate]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, results.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && results[activeIdx]) { handleSelect(results[activeIdx]); }
  };

  const typeIcon = {
    channel: '📡',
    vod: '🎬',
    playlist: '▶️',
    spot: '📢'
  };
  const typeLabel = {
    channel: 'Canal',
    vod: 'Video',
    playlist: 'Playlist',
    spot: 'Spot'
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-start justify-center pt-[15vh]"
      style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
      onClick={() => setOpen(false)}
    >
      <div
        className="w-full max-w-xl mx-4 glass-card border border-white/10 rounded-2xl overflow-hidden shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
          <Search size={18} className="text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Buscar canales, videos, playlists..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 bg-transparent text-white placeholder-gray-500 outline-none text-sm"
          />
          {loading && (
            <div className="w-4 h-4 border-2 border-indigo-400/50 border-t-indigo-400 rounded-full animate-spin flex-shrink-0" />
          )}
          <kbd className="text-[10px] text-gray-500 bg-white/5 border border-white/10 rounded px-1.5 py-0.5 flex-shrink-0">Esc</kbd>
        </div>

        {/* Results */}
        {results.length > 0 && (
          <div className="max-h-80 overflow-y-auto">
            {results.map((r, i) => (
              <div
                key={`${r.type}-${r.id}`}
                onClick={() => handleSelect(r)}
                className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${
                  i === activeIdx ? 'bg-indigo-500/20' : 'hover:bg-white/5'
                }`}
              >
                <span className="text-base w-6 text-center flex-shrink-0">{typeIcon[r.type]}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-white truncate">{r.name}</div>
                  {r.subtitle && <div className="text-xs text-gray-400 truncate">{r.subtitle}</div>}
                </div>
                <span className="text-[10px] text-gray-500 bg-white/5 rounded px-1.5 py-0.5 flex-shrink-0">
                  {typeLabel[r.type]}
                </span>
              </div>
            ))}
          </div>
        )}

        {query.length >= 2 && !loading && results.length === 0 && (
          <div className="px-4 py-6 text-center text-sm text-gray-500">
            Sin resultados para &ldquo;{query}&rdquo;
          </div>
        )}

        {query.length === 0 && (
          <div className="px-4 py-4 flex items-center justify-between">
            <span className="text-xs text-gray-500">Escribe al menos 2 caracteres para buscar</span>
            <div className="flex items-center gap-1 text-xs text-gray-600">
              <kbd className="bg-white/5 border border-white/10 rounded px-1.5 py-0.5">↑↓</kbd>
              <span>navegar</span>
              <kbd className="bg-white/5 border border-white/10 rounded px-1.5 py-0.5 ml-1">↵</kbd>
              <span>abrir</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Layout ─────────────────────────────────────────────────────────────────

function Layout({ children, user, onLogout }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [tooltipContent, setTooltipContent] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();

  // Prefetch server timezone on mount so NotificationPanel uses it from localStorage immediately.
  React.useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get('/api/settings', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (r.data?.timezone) localStorage.setItem('serverTz', r.data.timezone); })
      .catch(() => {});
  }, []);

  // Detectar dispositivos móviles
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      // Auto-collapse sidebar on mobile
      if (window.innerWidth <= 768) {
        setSidebarOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Cerrar sidebar en móvil al navegar
  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const menuItems = [
    { path: '/dashboard', icon: LayoutDashboard, key: 'dashboard' },
    { path: '/channels', icon: Radio, key: 'channels' },
    { path: '/vod', icon: Video, key: 'vod' },
    { path: '/playlists', icon: List, key: 'playlists' },
    { path: '/spots', icon: Megaphone, key: 'spots' },
    { path: '/avances', icon: Tv2, key: 'avances' },
    { path: '/simulcast', icon: Cast, key: 'simulcast' },
    { path: '/player', icon: PlayCircle, key: 'player' },
    { path: '/analytics', icon: BarChart3, key: 'analytics' },
    { path: '/settings', icon: Settings, key: 'settings' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-[#0B0F14]" style={{ fontFamily: 'Figtree, sans-serif' }}>
      {/* Mobile Header */}
      {isMobile && (
        <div className="fixed top-0 left-0 right-0 h-16 glass-card border-b border-black/10 dark:border-white/5
                        flex items-center justify-between px-4 z-[1001]">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 text-gray-600 dark:text-gray-300
                       hover:bg-black/10 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white transition-all duration-300"
          >
            <Menu size={24} />
          </button>

          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600
                            flex items-center justify-center">
              <img
                src="/logopanel.svg"
                alt="StreamCP Logo"
                className="w-5 h-5 object-contain"
              />
            </div>
            <span className="font-bold text-lg bg-gradient-to-r from-indigo-400 to-purple-400
                           bg-clip-text text-transparent">
              StreamCP
            </span>
          </div>

          <div className="flex items-center gap-2">
            <ThemeToggle />
          </div>
        </div>
      )}

      {/* Mobile sidebar backdrop */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[1001]"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Premium Glassmorphism Sidebar */}
      <aside className={`
        ${isMobile ? (sidebarOpen ? 'w-[280px]' : 'w-0') : 'w-[280px]'}
        glass-card border-r border-black/10 dark:border-white/5
        transition-all duration-300 ease-in-out
        fixed h-screen flex flex-col
        ${isMobile ? 'z-[1002]' : 'z-[100]'}
        ${isMobile ? (sidebarOpen ? 'translate-x-0' : '-translate-x-full') : 'translate-x-0'}
        top-0 left-0
      `}>

        {/* ── TOP: Logo ─────────────────────────────────────────────────── */}
        {!isMobile && (
          <div className="flex-shrink-0 px-4 py-4 border-b border-black/10 dark:border-white/5 flex items-center gap-3">
            <div className="w-9 h-9 flex-shrink-0 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600
                            flex items-center justify-center shadow-lg shadow-indigo-500/20">
              <img src="/logopanel.svg" alt="StreamCP" className="w-5 h-5 object-contain" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-base bg-gradient-to-r from-indigo-400 to-purple-400
                              bg-clip-text text-transparent truncate leading-tight">
                StreamCP
              </div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400">Pro v{version}</div>
            </div>
          </div>
        )}

        {/* Mobile logo area handled by mobile header above */}

        {/* ── MIDDLE: User + Nav (scrollable) ────────────────────────── */}
        <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'none' }}>
          {/* User Info */}
          {sidebarOpen ? (
            <div className="px-4 py-4 border-b border-black/10 dark:border-white/5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center
                              text-white font-semibold text-base flex-shrink-0
                              bg-gradient-to-br from-indigo-500 via-purple-500 to-emerald-500
                              shadow-lg glow-purple">
                {user?.full_name ? user.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) :
                 user?.email ? user.email.split('@')[0].slice(0, 2).toUpperCase() : 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-gray-900 dark:text-white overflow-hidden text-ellipsis whitespace-nowrap">
                  {user?.full_name || user?.email?.split('@')[0] || 'User'}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 overflow-hidden text-ellipsis whitespace-nowrap">
                  {user?.email}
                </div>
              </div>
            </div>
          ) : (
            <div className="py-3 border-b border-black/10 dark:border-white/5 flex justify-center">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center
                              text-white font-semibold text-xs flex-shrink-0
                              bg-gradient-to-br from-indigo-500 via-purple-500 to-emerald-500">
                {user?.full_name ? user.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) :
                 user?.email ? user.email.split('@')[0].slice(0, 2).toUpperCase() : 'U'}
              </div>
            </div>
          )}

          {/* Navigation Items */}
          <nav className={`py-3 relative overflow-visible ${sidebarOpen ? 'px-3' : 'px-3'}`}>
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <div key={item.path} className="relative mb-1">
                  <button
                    onClick={() => handleNavigation(item.path)}
                    className={`
                      sidebar-item w-full
                      ${sidebarOpen ? 'px-4 py-3' : 'px-3 py-3 justify-center'}
                      ${active ? 'active' : ''}
                    `}
                    onMouseEnter={(e) => {
                      if (!sidebarOpen && !isMobile) {
                        setHoveredItem(item.key);
                        setTooltipContent(t(item.key));
                        const rect = e.currentTarget.getBoundingClientRect();
                        setTooltipPosition({ top: rect.top + rect.height / 2, left: 90 });
                      }
                    }}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    <Icon size={20} className="flex-shrink-0" />
                    {sidebarOpen && (
                      <span className="text-sm font-medium flex-1 text-left">{t(item.key)}</span>
                    )}
                    {sidebarOpen && active && (
                      <ChevronRight size={16} className="flex-shrink-0 text-indigo-400" />
                    )}
                  </button>
                </div>
              );
            })}
          </nav>
        </div>

        {/* ── BOTTOM: Action icons ────────────────────────────────────── */}
        <div className={`flex-shrink-0 border-t border-black/10 dark:border-white/5 p-3
                         ${sidebarOpen ? '' : 'flex flex-col items-center gap-2'}`}>
          {sidebarOpen ? (
            <>
              {/* Row: Search + Bell + Theme */}
              <div className="flex items-center gap-2 mb-2">
                <button
                  onClick={() => document.dispatchEvent(new CustomEvent('streamcp:open-search'))}
                  className="flex-1 flex items-center gap-2 px-3 py-2 rounded-xl
                             bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10
                             text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white
                             hover:bg-black/10 dark:hover:bg-white/10 transition-all text-xs font-medium"
                  title="Búsqueda global (⌘K)"
                >
                  <Search size={14} />
                  <span>Buscar</span>
                  <kbd className="ml-auto text-[10px] bg-white/5 border border-white/10 rounded px-1 py-0.5 hidden sm:block">⌘K</kbd>
                </button>
                <BellButton />
                <ThemeToggle />
              </div>
              {/* Logout */}
              <button
                onClick={() => { onLogout(); navigate('/login'); }}
                className="w-full rounded-xl font-semibold text-white
                           bg-gradient-to-r from-red-600 to-red-700
                           hover:from-red-700 hover:to-red-800
                           shadow-lg hover:shadow-xl hover:shadow-red-500/30
                           transition-all duration-300
                           flex items-center justify-start gap-3 px-4 py-2.5
                           border border-red-500/20 glow-red"
              >
                <LogOut size={18} className="flex-shrink-0" />
                <span className="text-sm">{t('logout')}</span>
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => document.dispatchEvent(new CustomEvent('streamcp:open-search'))}
                className="p-2 rounded-lg bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10
                           text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-black/10 dark:hover:bg-white/10 transition-all"
                title="Búsqueda global (⌘K)"
              >
                <Search size={18} />
              </button>
              <BellButton compact />
              <ThemeToggle />
              <button
                onClick={() => { onLogout(); navigate('/login'); }}
                className="p-2 rounded-lg bg-red-600/20 border border-red-500/30
                           text-red-400 hover:bg-red-600/30 hover:text-red-300 transition-all"
                title={t('logout')}
              >
                <LogOut size={18} />
              </button>
            </>
          )}
        </div>
      </aside>

      {/* Main Content Area */}
      <main className={`
        flex-1 min-h-screen transition-all duration-300
        ${isMobile ? 'ml-0 pt-16' : 'ml-[280px]'}
      `}>
        <div className="p-4 sm:p-6 lg:p-10 max-w-[1600px] mx-auto">
          {children}
        </div>
      </main>


      {/* Global Search Palette */}
      <GlobalSearch />

      {/* Premium Tooltip (Desktop Collapsed Sidebar) */}
      {!sidebarOpen && !isMobile && hoveredItem && (
        <div
          className="fixed z-[9999] -translate-y-1/2 glass-card px-4 py-2.5 rounded-xl text-sm font-medium
                     text-white whitespace-nowrap border border-white/10 shadow-xl glow-indigo
                     animate-fadeIn pointer-events-none"
          style={{
            left: `${tooltipPosition.left}px`,
            top: `${tooltipPosition.top}px`
          }}
        >
          {tooltipContent}
          {/* Tooltip Arrow */}
          <div className="absolute -left-1.5 top-1/2 -translate-y-1/2 w-0 h-0
                          border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent
                          border-r-[6px] border-r-white/10" />
        </div>
      )}
    </div>
  );
}

export default Layout;
